<template>
  <div>
    <div
      v-if="expanded"
      class="mb-4 bg-gray-50 dark:bg-dark-box-background relative p-4 dark:border-dark-divider-border border rounded-md dark:text-white">
      <div class="flex items-center">
        <ToggleStatusButton :object="plan" :disable-toggle="true" />
        <div class="ml-2 flex">
          <div>{{ plan.name }}</div>
        </div>
        <div class="absolute -right-3 -top-3 flex gap-x-2">
          <div
            class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-gray-50 rounded-full border dark:bg-dark-screen-background"
            @click="expanded = !expanded">
            <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
            <span
              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
              {{ $t("Minimize") }}</span>
          </div>
          <div
            class="has-tooltip cursor-pointer w-7 h-7 bg-gray-50 rounded-full border dark:bg-dark-screen-background dark:border-dark-divider-border"
            @click="handleViewPlan(plan)">
            <Maximize2 class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration" color="#2DA7D7" />
            <span
              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
              {{ $t("View Plan") }}</span>
          </div>
        </div>
      </div>
      <div class="flex mt-5 w-full">
        <div class="mt-2 w-[40%] flex">
          <Badge :icon="goal_icon" :object="plan" :list-name="'goals'" />
          <Badge :icon="task_icon" :object="plan" :list-name="'tasks'" />
          <div v-if="!plan.goals.count && !plan.tasks.count" class="text-common-grey">
            {{ $t("No related goals or tasks") }}
          </div>
        </div>
        <div class="ml-20 w-[10%]">
          <div class="flex flex-row gap-2 items-center">
            <div class="text-common-grey whitespace-nowrap">
              {{ $t("Due Date") }}:
            </div>

            <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
              <span v-if="plan.due_date">
                {{ $moment(plan.due_date).format("DD MMM  YYYY") }}
              </span>

              <span v-else> - </span>
            </div>
          </div>
        </div>
        <div :class="[`${plan.companies.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
          <div
            v-if="plan.companies.length"
            :class="[`${plan.companies.length > 4 ? '' : '-ml-10'}`]"
            class="text-common-grey whitespace-nowrap text-start mb-2">
            {{ $t("Companies") }}
          </div>
          <div v-if="!plan.companies.length" class="text-common-grey mt-10">
            {{ $t("No companies") }}
          </div>
          <div class="flex -space-x-4 overflow-hidden items-center -ml-5">
            <span v-for="company in plan.companies.slice(0, 4)" :key="company.id" class="inline-block has-tooltip">
              <img v-if="company.avatar" class="inline-block h-9 w-9 rounded-full" :src="company.avatar" alt="">
              <Avatar v-else :first-name="Record.from(company).name" />
              <span class="tooltip whitespace-nowrap p-1 bg-gray-300 rounded-sm">
                <div class="">
                  <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                    {{ Record.from(company).name }}
                  </p>
                </div>
              </span>
            </span>
            <span v-if="plan.companies.length > 4" class="ml-auto relative left-6 text-sm text-light-brand-blue dark:text-dark-brand-blue">
              + {{ plan.companies.length - 4 }} {{ $t("more") }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="mb-4 flex align-middle relative bg-gray-50 dark:bg-dark-box-background p-4 dark:border-dark-divider-border border rounded-md dark:text-white">
      <div
        :class="[
          'w-4 h-4 rounded-full mt-1 mr-4 relative',
          {
            'bg-dark-brand-blue': ['in-progress', 'pending'].includes(plan.status),
            'bg-status-warning-yellow': plan.status == 'overdue',
            'bg-status-success-green': plan.status == 'completed',
            'bg-status-error-red': ['cancelled', 'missed'].includes(plan.status)
          }
        ]" />
      <div class="ml-2 flex">
        <div>{{ plan.name }}</div>
        <ClipboardCheck class="absolute opacity-0 pointer-events-none" />
        <Badge :icon="goal_icon" :object="plan" :list-name="'goals'" />
        <Badge :icon="task_icon" :object="plan" :list-name="'tasks'" />
      </div>
      <div class="ml-auto mr-20">
        <div v-if="plan?.record?.values[plan.filter?.split(' ')[1]]" class="ml-auto text-md text-common-grey">
          {{
            Math.round(
              (parseInt(plan?.record.values[plan.filter.split(" ")[1]]) /
                parseInt(plan.filter.split(" ")[plan.filter.split(" ").length - 2])) *
                100
            )
          }}%
        </div>
      </div>
      <div class="absolute -right-3 -top-3 flex gap-x-2">
        <div
          class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-gray-50 rounded-full border dark:bg-dark-screen-background"
          @click="expanded = !expanded">
          <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
          <span
            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
            {{ $t("Expand") }}</span>
        </div>
        <div
          class="has-tooltip cursor-pointer w-7 h-7 bg-gray-50 rounded-full border dark:bg-dark-screen-background dark:border-dark-divider-border"
          @click="handleViewPlan(plan)">
          <Maximize2 class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration" color="#2DA7D7" />
          <span
            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
            {{ $t("View Plan") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Badge from "@/components/shared/ActivityStatusBadge.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { Maximize2, ClipboardCheck, Star } from "lucide-vue-next"
import ToggleStatusButton from "@/components/shared/ToggleStatusButton.vue"
import { Record } from "@/models/record"
import { markRaw } from "vue"

export default {
  components: {
    ToggleStatusButton,
    Avatar,
    Maximize2,
    Badge: markRaw(Badge),
    ClipboardCheck: markRaw(ClipboardCheck)
  },
  props: {
    plan: {
      type: Object,
      required: true
    }
  },
  emits: [],
  data() {
    return {
      task_icon: ClipboardCheck,
      goal_icon: Star,
      Record,
      expanded: false
    }
  },
  watch: {},

  created() {},

  methods: {
    handleViewPlan(plan) {
      this.$router.push(`${this.$route.path}/${plan.id}`)
    }
  }
}
</script>
